/*
 * pickup-rules.js — motor ÚNICO de reglas de recogida (Say Cheese)
 *
 * Fuente de verdad compartida por:
 *   - snippets/pickup-selector.liquid  (pinta el calendario en la ficha de producto)
 *   - assets/pickup-cart-guard.js      (revalida el carrito antes del checkout)
 *
 * Nunca duplicar estas reglas en otro sitio: si divergen, el carrito bloquea
 * fechas que la ficha permitió (o al revés) y el cliente se queda atascado.
 *
 * Config inyectada desde Ajustes del tema en layout/theme.liquid → window.PICKUP_CONFIG
 */
window.PickupRules = (function () {
  'use strict';

  /*
   * La config se lee en cada llamada, no al cargar: los scripts van con defer y la
   * ficha de producto añade sus propias fechas bloqueadas después del <head>.
   */
  function cfg() {
    return window.PICKUP_CONFIG || {};
  }
  function cutoffHour() {
    return typeof cfg().cutoffHour === 'number' ? cfg().cutoffHour : 13;
  }
  function sundayCutoffHour() {
    return typeof cfg().sundayCutoffHour === 'number' ? cfg().sundayCutoffHour : 10;
  }
  function saturdayCutoffHour() {
    return typeof cfg().saturdayCutoffHour === 'number' ? cfg().saturdayCutoffHour : 18;
  }
  function blockedDates() {
    return cfg().blockedDates || [];
  }

  var DAY_NAMES = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
  var MONTH_NAMES = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
  ];

  function startOfDay(d) {
    var x = new Date(d.getTime());
    x.setHours(0, 0, 0, 0);
    return x;
  }

  /* Fecha local en AAAA-MM-DD. Nunca usar toISOString(): convierte a UTC y resta un día. */
  function toISO(d) {
    return (
      d.getFullYear() +
      '-' +
      String(d.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(d.getDate()).padStart(2, '0')
    );
  }

  /* new Date('2026-07-29') se parsea como UTC → construir por componentes. */
  function fromISO(s) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || '').trim());
    if (!m) return null;
    var d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return isNaN(d.getTime()) ? null : d;
  }

  function formatLabel(d) {
    return DAY_NAMES[d.getDay()] + ' ' + d.getDate() + ' ' + MONTH_NAMES[d.getMonth()];
  }

  /*
   * Lee el texto que se guarda en la propiedad "Día de recogida" ("miércoles 29 julio").
   * Se guarda SIN año, así que el año se deduce por el nombre del día de la semana:
   * se prueban año-1, año y año+1 y gana el que cuadre y esté más cerca de hoy.
   * Sin esto, un carrito con fecha del pasado se leería como del año que viene
   * y pasaría la validación (justo el caso que queremos cazar).
   */
  function parseLabel(text, now) {
    now = now || new Date();
    var t = String(text || '').toLowerCase();
    if (!t) return null;

    var month = -1;
    for (var i = 0; i < MONTH_NAMES.length; i++) {
      if (t.indexOf(MONTH_NAMES[i]) !== -1) month = i;
    }
    var dayMatch = t.match(/\d{1,2}/);
    if (month < 0 || !dayMatch) return null;
    var dayOfMonth = parseInt(dayMatch[0], 10);
    if (!dayOfMonth || dayOfMonth > 31) return null;

    var expected = -1;
    for (var j = 0; j < DAY_NAMES.length; j++) {
      if (t.indexOf(DAY_NAMES[j]) !== -1) expected = j;
    }

    var best = null;
    for (var y = now.getFullYear() - 1; y <= now.getFullYear() + 1; y++) {
      var cand = new Date(y, month, dayOfMonth);
      if (cand.getMonth() !== month) continue; // 31 de un mes de 30
      if (expected >= 0 && cand.getDay() !== expected) continue;
      if (!best || Math.abs(cand - now) < Math.abs(best - now)) best = cand;
    }
    return best;
  }

  /* Reglas de qué días se puede recoger. `now` inyectable para poder testearlo. */
  function isDateEnabled(date, point, now) {
    if (!date) return false;
    now = now || new Date();

    var today = startOfDay(now);
    var selected = startOfDay(date);
    var day = selected.getDay();
    var diffDays = Math.round((selected - today) / 86400000);

    if ((day === 0 || day === 1) && point === 'Obrador') return false; // el obrador no abre domingo ni lunes

    // Lunes: los pedidos se cierran el sábado a la hora de corte; el domingo ya no se puede.
    if (day === 1 && now.getDay() === 6 && now.getHours() >= saturdayCutoffHour() && diffDays === 2) return false;
    if (day === 1 && now.getDay() === 0 && diffDays === 1) return false;

    // Domingo pasada la hora de corte: el martes ya no da tiempo.
    if (now.getDay() === 0 && now.getHours() >= sundayCutoffHour() && day === 2 && diffDays === 2) {
      return false;
    }
    // Lunes: no se prepara nada, así que mañana (martes) tampoco.
    if (now.getDay() === 1 && diffDays === 1) return false;

    if (diffDays <= 0) return false; // hoy, o una fecha ya pasada
    if (diffDays === 1 && now.getHours() >= cutoffHour()) return false; // corte para mañana

    if (blockedDates().indexOf(toISO(selected)) !== -1) return false; // vacaciones / cierres

    return true;
  }

  /* Franjas de atención por punto y día de la semana. */
  function timeRanges(date, point) {
    if (!date) return [];
    var day = date.getDay();

    if (point === 'Obrador') return [['10:00', '14:00']];
    if (day === 1 || day === 2 || day === 3 || day === 4) return [['10:30', '14:00'], ['17:30', '20:30']];
    if (day === 5 || day === 6) return [['10:30', '14:00'], ['17:30', '21:00']];
    if (day === 0) return [['11:00', '14:00'], ['16:00', '20:00']];
    return [];
  }

  function expandRange(from, to, step) {
    step = step || 30;
    var times = [];
    var parts = from.split(':').map(Number);
    var h = parts[0];
    var m = parts[1];
    var end = to.split(':').map(Number);
    var endH = end[0];
    var endM = end[1];

    while (h < endH || (h === endH && m < endM)) {
      times.push(String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0'));
      m += step;
      if (m >= 60) {
        h++;
        m = m % 60;
      }
    }
    var endTime = String(endH).padStart(2, '0') + ':' + String(endM).padStart(2, '0');
    if (times.indexOf(endTime) === -1) times.push(endTime);
    return times;
  }

  function timeOptions(date, point) {
    var out = [];
    timeRanges(date, point).forEach(function (r) {
      expandRange(r[0], r[1]).forEach(function (t) {
        if (out.indexOf(t) === -1) out.push(t);
      });
    });
    return out;
  }

  function isTimeEnabled(date, point, time) {
    if (!time) return false;
    return timeOptions(date, point).indexOf(time) !== -1;
  }

  /*
   * Valida una línea de carrito ya existente.
   * Devuelve {ok, reason, date}. IMPORTANTE: si no hay datos suficientes para
   * decidir devuelve ok:true (fail-open) — nunca bloquear una venta por una duda.
   */
  function validateLine(props, now) {
    props = props || {};
    now = now || new Date();

    var label = props['Día de recogida'];
    if (!label) return { ok: true, reason: 'sin-fecha' }; // cookies y demás: no aplica

    var point = props['Punto de recogida'] || '';
    var time = props['Hora de recogida'] || '';
    var date = fromISO(props['_fecha_iso']) || parseLabel(label, now);

    if (!date) return { ok: true, reason: 'fecha-ilegible' }; // fail-open

    if (!isDateEnabled(date, point, now)) {
      return { ok: false, reason: 'fecha', date: date };
    }
    // Sin punto no se sabe qué horario aplica: no se juzga la hora (fail-open).
    if (point && time && !isTimeEnabled(date, point, time)) {
      return { ok: false, reason: 'hora', date: date };
    }
    return { ok: true, date: date };
  }

  return {
    DAY_NAMES: DAY_NAMES,
    MONTH_NAMES: MONTH_NAMES,
    cutoffHour: cutoffHour,
    sundayCutoffHour: sundayCutoffHour,
    saturdayCutoffHour: saturdayCutoffHour,
    blockedDates: blockedDates,
    startOfDay: startOfDay,
    toISO: toISO,
    fromISO: fromISO,
    formatLabel: formatLabel,
    parseLabel: parseLabel,
    isDateEnabled: isDateEnabled,
    timeRanges: timeRanges,
    timeOptions: timeOptions,
    isTimeEnabled: isTimeEnabled,
    validateLine: validateLine,
  };
})();
